import { motion } from "framer-motion";
import { Mail, Phone, MapPin } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_green-creations/artifacts/cf3sam7y_Untitled%20design.png";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.12, duration: 0.6, ease: "easeOut" },
  }),
};

export default function Contact() {
  return (
    <div data-testid="contact-page">
      {/* Page Header */}
      <section className="py-20 md:py-28 bg-[#EAE5D9]" data-testid="contact-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-col md:flex-row md:items-center md:justify-between gap-8"
          >
            <div className="flex-1">
              <p className="text-[#8B5E34] font-body text-sm tracking-widest uppercase mb-4">
                Say Hello
              </p>
              <h1 className="font-heading text-5xl sm:text-7xl tracking-tighter font-light text-[#222522]">
                Let&apos;s Chat
              </h1>
              <p className="font-body text-base md:text-lg text-[#5C635B] mt-6 max-w-2xl leading-relaxed">
                Got an idea? Want something built? Just fancy a natter about reclaimed wood? 
                We&apos;re all ears — and usually covered in sawdust.
              </p>
            </div>
            <img
              src={LOGO_URL}
              alt="Small Feet"
              className="h-28 md:h-36 w-auto object-contain rounded flex-shrink-0"
              style={{ mixBlendMode: "multiply" }}
              data-testid="contact-logo"
            />
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24 md:py-32" data-testid="contact-content-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
            {/* Body Text */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={0}
              variants={fadeUp}
              className="md:col-span-7"
            >
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-light text-[#222522] mb-6">
                We don&apos;t do ordinary
              </h2>
              <p className="font-body text-base text-[#5C635B] leading-relaxed mb-5">
                Whether you&apos;ve got a sketch on a napkin, a photo of something you saw 
                in a country pub, or just a vague feeling that your garden needs &ldquo;something 
                a bit different&rdquo; — get in touch. We&apos;ve built everything from teapot 
                birdhouses to double decker cafe buses, so trust us when we say: 
                there&apos;s no such thing as too weird.
              </p>
              <p className="font-body text-base text-[#5C635B] leading-relaxed mb-5">
                We work with whatever reclaimed materials we can get our hands on — old timber, 
                salvaged windows, forgotten doors, bits of corrugated sheeting that have been 
                sat in a barn for 40 years. If it&apos;s got character and it&apos;s still standing, 
                we&apos;ll find a use for it.
              </p>
              <p className="font-body text-base text-[#5C635B] leading-relaxed mb-5">
                Every project starts with a conversation. No hard sell, no jargon, just a 
                chat about what you&apos;re after and how we can make it happen. We&apos;ll 
                give you honest advice, a rough idea of cost, and probably make you a brew 
                if you visit the workshop.
              </p>
              <p className="font-body text-base text-[#5C635B] leading-relaxed">
                Based in Leicester but happy to travel for the right project. We&apos;ve 
                delivered across the Midlands and beyond — if you can describe it, 
                we can probably build it. And if we can&apos;t, we&apos;ll have a good 
                laugh trying.
              </p>
            </motion.div>

            {/* Contact Details */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={1}
              variants={fadeUp}
              className="md:col-span-5"
            >
              <div className="bg-[#EAE5D9] p-8 md:p-10 rounded" data-testid="contact-details-card">
                <h3 className="font-heading text-2xl text-[#222522] mb-2">
                  Get Hold of Us
                </h3>
                <p className="font-body text-sm text-[#5C635B] mb-6">
                  The quickest way to reach us is by email or phone. 
                  We&apos;re usually in the workshop, so don&apos;t be shy.
                </p>
                <div className="space-y-5">
                  <a
                    href="mailto:smallfeet@mtloop.co.uk"
                    className="flex items-start gap-4 group"
                    data-testid="contact-email-link"
                  >
                    <div className="w-10 h-10 bg-[#3A5A3F] flex items-center justify-center rounded flex-shrink-0">
                      <Mail size={18} className="text-[#F9F8F5]" />
                    </div>
                    <div>
                      <p className="font-body text-xs text-[#5C635B] tracking-wider uppercase">
                        Email
                      </p>
                      <p className="font-body text-[#222522] group-hover:text-[#8B5E34] transition-colors">
                        smallfeet@mtloop.co.uk
                      </p>
                    </div>
                  </a>

                  <a
                    href="tel:07979119771"
                    className="flex items-start gap-4 group"
                    data-testid="contact-phone-link"
                  >
                    <div className="w-10 h-10 bg-[#3A5A3F] flex items-center justify-center rounded flex-shrink-0">
                      <Phone size={18} className="text-[#F9F8F5]" />
                    </div>
                    <div>
                      <p className="font-body text-xs text-[#5C635B] tracking-wider uppercase">
                        Mobile
                      </p>
                      <p className="font-body text-[#222522] group-hover:text-[#8B5E34] transition-colors">
                        07979 119 771
                      </p>
                    </div>
                  </a>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#3A5A3F] flex items-center justify-center rounded flex-shrink-0">
                      <MapPin size={18} className="text-[#F9F8F5]" />
                    </div>
                    <div>
                      <p className="font-body text-xs text-[#5C635B] tracking-wider uppercase">
                        Location
                      </p>
                      <p className="font-body text-[#222522]">
                        Leicester, UK
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-[#D5CFBF]">
                  <p className="font-body text-sm text-[#5C635B] leading-relaxed">
                    We usually get back within 24 hours. If it&apos;s urgent, 
                    give us a bell — we&apos;re probably in the workshop.
                  </p>
                </div>
              </div>

              {/* eBay Shop Link */}
              <div className="mt-6 bg-[#EAE5D9] p-8 md:p-10 rounded" data-testid="ebay-card">
                <h3 className="font-heading text-2xl text-[#222522] mb-2">
                  Shop Online
                </h3>
                <p className="font-body text-sm text-[#5C635B] mb-4">
                  Browse our ready-made pieces on eBay. New items added regularly — 
                  if you see something you like, grab it quick.
                </p>
                <a
                  href="https://www.ebay.co.uk/usr/small_feet"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary px-6 py-3 text-sm font-body tracking-wider uppercase inline-flex items-center gap-2"
                  data-testid="contact-ebay-link"
                >
                  Visit Our eBay Shop
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
